import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useRef } from 'react';

const HeroSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Parallax layers - DESKTOP ONLY
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const gridY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '80%']);
  const diagramY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);
  
  const contentOpacity = useTransform(scrollYProgress, [0, 0.2, 0.4], [1, 0.4, 0]);
  const diagramOpacity = useTransform(scrollYProgress, [0, 0.15, 0.4], [0.5, 1, 1]);
  const diagramScale = useTransform(scrollYProgress, [0, 0.3, 0.6], [0.75, 1, 1.3]);
  
  const glowIntensity = useTransform(scrollYProgress, [0, 0.4], [0.15, 0.4]);

  return (
    <section ref={sectionRef} className="relative min-h-[100vh]">
      {/* Sticky container for pinned effect - DESKTOP ONLY */}
      <div className="lg:sticky lg:top-0 lg:h-screen flex items-center justify-center overflow-hidden min-h-screen lg:min-h-0">
        
        {/* Deep background - DESKTOP parallax */}
        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 bg-[#080808] lg:block"
        />

        {/* Static background for mobile - instant load */}
        <div className="lg:hidden absolute inset-0 bg-[#080808]" />

        {/* Ambient glow - DESKTOP ONLY - removed from mobile for performance */}
        <motion.div
          style={{ y: gridY }}
          className="hidden lg:block absolute inset-0 pointer-events-none overflow-hidden"
        >
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            style={{ opacity: glowIntensity }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-[5%] left-[-10%] w-[500px] h-[500px] lg:w-[700px] lg:h-[700px] rounded-full bg-accent-blue blur-[120px] lg:blur-[160px]"
          />
          <motion.div
            animate={{ scale: [1, 1.12, 1] }}
            style={{ opacity: useTransform(scrollYProgress, [0, 0.4], [0.08, 0.25]) }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
            className="absolute bottom-[0%] right-[0%] w-[400px] h-[400px] lg:w-[600px] lg:h-[600px] rounded-full bg-cyan-400 blur-[140px] lg:blur-[180px]"
          />
        </motion.div>

        {/* Simplified static glow for mobile - lightweight */}
        <div className="lg:hidden absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[5%] left-[-10%] w-[300px] h-[300px] rounded-full bg-accent-blue blur-[80px] opacity-12" />
          <div className="absolute bottom-[0%] right-[0%] w-[250px] h-[250px] rounded-full bg-cyan-400 blur-[90px] opacity-8" />
        </div>

        {/* Grid overlay - DESKTOP ONLY */}
        <motion.div
          style={{ y: gridY, opacity: useTransform(scrollYProgress, [0, 0.4], [0.04, 0.1]) }}
          className="hidden lg:block absolute inset-0 pointer-events-none"
        >
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`,
              backgroundSize: '80px 80px',
            }}
          />
        </motion.div>

        {/* DESKTOP LAYOUT */}
        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="hidden lg:flex max-w-[1400px] mx-auto px-8 relative z-10 items-center justify-between gap-16 w-full"
        >
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 max-w-[580px]"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm mb-2"
            >
              <span className="w-2 h-2 rounded-full bg-accent-blue animate-pulse" />
              <span className="text-sm text-gray-300 font-medium tracking-widest uppercase">Strategic Advisory</span>
            </motion.div>

            <h1 className="font-bold text-white leading-[1.05] tracking-tight mb-8 text-[64px] lg:text-[72px]">
              {['Remove', 'Growth', 'Bottlenecks'].map((word, wi) => (
                <motion.span
                  key={word}
                  className="block"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 + wi * 0.15, ease: [0.16, 1, 0.3, 1] }}
                >
                  {word}
                </motion.span>
              ))}
            </h1>

            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="origin-left h-[3px] w-28 rounded-full mb-8"
              style={{ background: 'linear-gradient(90deg, #0066FF, #00CFFF, transparent)' }}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-xl text-white font-semibold mb-6 leading-tight"
            >
              ForceM helps founders identify and remove the structural bottlenecks limiting growth.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="text-base text-gray-400 leading-relaxed max-w-[500px] mb-10"
            >
              Bad names. Weak positioning. Wrong markets. Broken pricing. Unclear narratives.<br />
              These are often the bottlenecks limiting growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-4"
            >
              <Link
                to="/apply"
                className="inline-block px-8 py-4 bg-accent-blue text-white text-base font-semibold rounded-full hover:brightness-125 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(0,102,255,0.5)] whitespace-nowrap cursor-pointer"
              >
                Get a Growth Bottleneck Audit
              </Link>
              <Link
                to="/frameworks"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                View Frameworks
                <i className="ri-arrow-right-line" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right diagram */}
          <motion.div
            style={{ 
              y: diagramY,
              scale: diagramScale,
              opacity: diagramOpacity
            }}
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 0.5, scale: 0.75 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 relative max-w-[600px]"
          >
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                animate={{ opacity: [0.15, 0.3, 0.15] }}
                style={{ scale: useTransform(scrollYProgress, [0, 0.4], [1, 1.6]) }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="w-[400px] h-[400px] rounded-full bg-accent-blue blur-[100px]"
              />
            </div>

            <svg viewBox="0 0 600 520" className="w-full h-auto relative z-10">
              <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0066FF" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#00CFFF" stopOpacity="1" />
                </linearGradient>
                <linearGradient id="nodeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1a1a2e" />
                  <stop offset="100%" stopColor="#16213e" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="glowStrong">
                  <feGaussianBlur stdDeviation="5" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {[
                { cy: 80, label: 'Team' },
                { cy: 180, label: 'Capital' },
                { cy: 280, label: 'Product' },
                { cy: 380, label: 'Market' },
              ].map(({ cy, label }, i) => (
                <motion.g
                  key={label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
                >
                  <circle cx="80" cy={cy} r="38" fill="url(#nodeGrad)" stroke="#333" strokeWidth="1.5" />
                  <circle cx="80" cy={cy} r="38" fill="none" stroke="#0066FF" strokeWidth="1" opacity="0.4" />
                  <text x="80" y={cy + 5} textAnchor="middle" fill="#e0e0e0" fontSize="13" fontWeight="600">
                    {label}
                  </text>
                </motion.g>
              ))}

              {[80, 180, 280, 380].map((cy, i) => (
                <motion.path
                  key={cy}
                  d={`M 118 ${cy} Q 190 ${cy} 215 250`}
                  stroke="#0066FF"
                  strokeWidth="1.5"
                  fill="none"
                  strokeDasharray="6,4"
                  opacity="0.5"
                  animate={{ strokeDashoffset: [0, -20] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', delay: i * 0.3 }}
                />
              ))}

              <motion.g
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1 }}
              >
                <rect x="205" y="210" width="80" height="80" rx="8" fill="#1a0a0a" stroke="#FF3B30" strokeWidth="2" />
                <motion.rect
                  x="205" y="210" width="80" height="80" rx="8"
                  fill="#FF3B30"
                  animate={{ opacity: [0.08, 0.2, 0.08] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <text x="245" y="246" textAnchor="middle" fill="#FF5A50" fontSize="11" fontWeight="700" filter="url(#glow)">
                  GROWTH
                </text>
                <text x="245" y="262" textAnchor="middle" fill="#FF5A50" fontSize="11" fontWeight="700" filter="url(#glow)">
                  BOTTLENECK
                </text>
              </motion.g>

              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <motion.path
                  d="M 310 250 L 360 250"
                  stroke="#00CFFF"
                  strokeWidth="4"
                  strokeLinecap="round"
                  filter="url(#glowStrong)"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <motion.polygon
                  points="356,243 372,250 356,257"
                  fill="#00CFFF"
                  filter="url(#glowStrong)"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <text x="335" y="238" textAnchor="middle" fill="#00CFFF" fontSize="11" fontWeight="700" letterSpacing="1.5" filter="url(#glow)">
                  REMOVE
                </text>
              </motion.g>

              {[
                { cy: 120, label: 'Revenue', delay: 1.5 },
                { cy: 250, label: 'Growth', delay: 1.65 },
                { cy: 380, label: 'Scale', delay: 1.8 },
              ].map(({ cy, label, delay }) => (
                <motion.g key={label}>
                  <motion.path
                    d={`M 372 250 Q 420 250 460 ${cy}`}
                    stroke="url(#gradient1)"
                    strokeWidth="2.5"
                    fill="none"
                    filter="url(#glow)"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.8, delay }}
                  />
                  <motion.g
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: delay + 0.6 }}
                  >
                    <rect x="460" y={cy - 22} width="110" height="44" rx="22" fill="#0a1628" stroke="#0066FF" strokeWidth="1.5" />
                    <motion.rect
                      x="460" y={cy - 22} width="110" height="44" rx="22"
                      fill="#0066FF"
                      animate={{ opacity: [0.08, 0.18, 0.08] }}
                      transition={{ duration: 2.5, repeat: Infinity, delay: delay }}
                    />
                    <text x="515" y={cy + 5} textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="700" filter="url(#glow)">
                      {label}
                    </text>
                  </motion.g>
                </motion.g>
              ))}

              <motion.g
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.2 }}
              >
                <text x="300" y="490" textAnchor="middle" fill="#555" fontSize="11" fontWeight="500" letterSpacing="3">
                  BOTTLENECK REMOVAL SYSTEM
                </text>
              </motion.g>
            </svg>
          </motion.div>
        </motion.div>

        {/* MOBILE LAYOUT - OPTIMIZED FOR SPEED */}
        <div className="lg:hidden w-full px-5 relative z-10 flex flex-col justify-start pt-[90px] pb-16">
          <div className="w-full max-w-[480px] mx-auto">
            {/* Badge - instant load, always visible */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-500/40 bg-blue-900/30 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-blue flex-shrink-0" />
              <span className="text-xs text-white font-semibold tracking-widest uppercase">Strategic Advisory</span>
            </div>

            {/* Headline - instant load */}
            <h1 className="font-bold text-white leading-[1.1] tracking-tight mb-5 text-[38px]">
              Remove<br />Growth<br />Bottlenecks
            </h1>

            {/* Accent line - instant load */}
            <div className="h-[2px] w-20 rounded-full mb-5" style={{ background: 'linear-gradient(90deg, #0066FF, #00CFFF, transparent)' }} />

            {/* Subheadline - instant load */}
            <p className="text-[16px] text-white font-semibold mb-4 leading-[1.6]">
              ForceM helps founders identify and remove the structural bottlenecks limiting growth.
            </p>

            {/* Supporting examples - instant load */}
            <p className="text-[15px] text-gray-400 leading-[1.7] mb-7">
              Bad names. Weak positioning. Wrong markets. Broken pricing. Unclear narratives. These are often the bottlenecks limiting growth.
            </p>

            {/* CTA buttons - instant load */}
            <div className="flex flex-col gap-3 mb-10">
              <Link
                to="/apply"
                className="w-full text-center px-6 py-4 bg-accent-blue text-white text-[16px] font-semibold rounded-full active:scale-95 transition-transform duration-150 whitespace-nowrap cursor-pointer min-h-[52px] flex items-center justify-center"
              >
                Get a Bottleneck Audit
              </Link>
              <Link
                to="/frameworks"
                className="w-full text-center inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/10 text-gray-300 active:bg-white/5 transition-colors duration-150 text-[15px] font-medium rounded-full whitespace-nowrap cursor-pointer min-h-[52px]"
              >
                View Frameworks
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator - desktop only */}
        <motion.div
          style={{ opacity: useTransform(scrollYProgress, [0, 0.1, 0.5], [1, 1, 0]) }}
          className="hidden lg:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 z-10"
        >
          <span className="text-xs text-gray-500 tracking-widest uppercase">Scroll to Expand</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-px h-10 bg-gradient-to-b from-gray-500 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;